'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LayoutDashboard, RefreshCcw, FileText, Users, BarChart3, Bell, Settings, ShieldCheck, ChevronLeft, ChevronRight, LogOut, ClipboardList,  } from 'lucide-react';
import { useAppraisalStore } from '@/store/appraisalStore';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
  group: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', href: '/', icon: <LayoutDashboard size={18} />, group: 'Overview' },
  { id: 'nav-cycles', label: 'Appraisal Cycles', href: '/appraisal-cycle-management', icon: <RefreshCcw size={18} />, badge: 3, group: 'Management' },
  { id: 'nav-templates', label: 'Templates & Questions', href: '/question-template-management', icon: <FileText size={18} />, group: 'Management' },
  { id: 'nav-employees', label: 'Employee Assignments', href: '/employee-assignments', icon: <Users size={18} />, badge: 9, group: 'Management' },
  { id: 'nav-submissions', label: 'Submissions & Reviews', href: '/submissions', icon: <ClipboardList size={18} />, badge: 14, group: 'Management' },
  { id: 'nav-reports', label: 'Reports & Analytics', href: '/reports', icon: <BarChart3 size={18} />, group: 'Insights' },
  { id: 'nav-notifications', label: 'Notifications', href: '/notifications', icon: <Bell size={18} />, badge: 5, group: 'Insights' },
  { id: 'nav-audit', label: 'Audit & Compliance', href: '/audit', icon: <ShieldCheck size={18} />, group: 'Insights' },
  { id: 'nav-settings', label: 'Configuration', href: '/configuration', icon: <Settings size={18} />, group: 'System' },
];

const GROUPS = ['Overview', 'Management', 'Insights', 'System'];

export default function AppSidebar() {
  const pathname = usePathname();
  const { sidebarCollapsed, setSidebarCollapsed } = useAppraisalStore();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname === href || pathname.startsWith(href + '/');
  };

  return (
    <aside
      className={`pn-appraisal__sidebar${sidebarCollapsed ? ' pn-appraisal__sidebar--collapsed' : ''}`}
    >
      {/* Brand */}
      <div className="pn-appraisal__sidebar-brand">
        <div className="pn-appraisal__sidebar-brand-logo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
        </div>
        <span className="pn-appraisal__sidebar-brand-name">
          PN<span>Appraisal</span>
        </span>
      </div>

      {/* Toggle button */}
      <button
        className="pn-appraisal__sidebar-toggle"
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {sidebarCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>

      {/* Nav */}
      <nav className="pn-appraisal__sidebar-nav">
        {GROUPS.map((group) => {
          const items = NAV_ITEMS.filter((i) => i.group === group);
          if (!items.length) return null;
          return (
            <div key={`group-${group}`} className="pn-appraisal__sidebar-group">
              <div className="pn-appraisal__sidebar-group-label">{group}</div>
              {items.map((item) => (
                <Link
                  key={item.id}
                  href={item.href}
                  className={`pn-appraisal__sidebar-nav-item${isActive(item.href) ? ' pn-appraisal__sidebar-nav-item--active' : ''}`}
                  data-tooltip={sidebarCollapsed ? item.label : undefined}
                >
                  <span className="pn-appraisal__sidebar-nav-item-icon">{item.icon}</span>
                  <span className="pn-appraisal__sidebar-nav-item-label">{item.label}</span>
                  {item.badge && (
                    <span className="pn-appraisal__sidebar-badge">{item.badge}</span>
                  )}
                </Link>
              ))}
            </div>
          );
        })}
      </nav>

      {/* User */}
      <div className="pn-appraisal__sidebar-user">
        <div className="pn-appraisal__sidebar-user-avatar">PN</div>
        <div className="pn-appraisal__sidebar-user-info">
          <div className="pn-appraisal__sidebar-user-info-name">Priya Nair</div>
          <div className="pn-appraisal__sidebar-user-info-role">HR Administrator</div>
        </div>
        <button
          className="pn-appraisal__btn pn-appraisal__btn--ghost pn-appraisal__btn--icon"
          style={{ color: 'rgba(148,163,184,0.7)', marginLeft: 'auto' }}
          data-tooltip="Sign out"
          aria-label="Sign out"
        >
          <LogOut size={15} />
        </button>
      </div>
    </aside>
  );
}